import {
  CloudBlankIcon,
  CloudRainingIcon,
  SunIcon,
  ThermometerWarmIcon,
  WindIcon,
} from '@/common/assets';
import { useNewTranslations } from '@/common/translations';

import { WeatherConditions } from './OtherMatchInformation.constants';
import type { WeatherCondition } from './OtherMatchInformation.types';
import AdditionalInformationSection from './AdditionalInformationSection';
import StadiumInformation from './StadiumInformation';

interface OtherMatchInformationProps {
  stadium?: {
    /** Name of the stadium. */
    name: string;
    /** Stadium capacity (number of seats). */
    capacity?: number | null;
  };
  referee?: {
    /** Name of the referee. */
    name: string;
    /** URL of the flag icon */
    flagUrl: string | null;
  };
  weather?: {
    /** Weather condition, e.g. "Clear Sky", "Rainy", "Windy" etc. */
    condition: WeatherCondition;
    /** Temperature in degrees Celsius. */
    temperature: number;
  };
}

const weatherConditionMap = {
  [WeatherConditions.CLEAR_SKY]: {
    icon: SunIcon,
    translationCode: 'PreMatchStats.ClearSky',
  },
  [WeatherConditions.CLOUDY]: {
    icon: CloudBlankIcon,
    translationCode: 'PreMatchStats.Cloudy',
  },
  [WeatherConditions.RAINY]: {
    icon: CloudRainingIcon,
    translationCode: 'PreMatchStats.Rainy',
  },
  [WeatherConditions.WINDY]: {
    icon: WindIcon,
    translationCode: 'PreMatchStats.Windy',
  },
};

export default function OtherMatchInformation({
  stadium,
  referee,
  weather,
}: OtherMatchInformationProps) {
  const translate = useNewTranslations();

  const { icon: WeatherIcon, translationCode: weatherConditionTranslationCode } =
    (weather && weatherConditionMap[weather.condition]) || {};

  return (
    <div className="flex flex-col gap-[8px]">
      {stadium && <StadiumInformation name={stadium.name} capacity={stadium.capacity} />}
      {(referee || weather) && (
        <div className="flex gap-[12px]">
          {referee && (
            <AdditionalInformationSection
              className="flex-1"
              contentClassName="gap-1"
              label={translate('PreMatchStats.Referee')}
            >
              {referee.flagUrl && (
                <img
                  className="shrink-0 w-[16px] h-[16px] p-px"
                  src={referee.flagUrl}
                  alt={`${referee.name}'s nationality flag`}
                />
              )}
              <span className="min-w-0 wrap-break-word">{referee.name}</span>
            </AdditionalInformationSection>
          )}
          {weather && (
            <AdditionalInformationSection
              className="flex-1"
              contentClassName="gap-1"
              label={translate('PreMatchStats.Weather')}
            >
              {WeatherIcon && <WeatherIcon className="shrink-0 w-[12px] h-[12px]" />}
              <span className="min-w-0 wrap-break-word">
                {translate(weatherConditionTranslationCode)}
              </span>
              <span className="text-pmw-label-tertiary-light dark:text-pmw-label-tertiary-dark">
                -
              </span>
              <ThermometerWarmIcon className="shrink-0 w-[12px] h-[12px]" />
              <span>{weather.temperature}°C</span>
            </AdditionalInformationSection>
          )}
        </div>
      )}
    </div>
  );
}
